import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Star, User, FileSignature, FolderOpen, Users } from 'lucide-react';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { Card } from '../components';
import { getGecontracteerdeProfessionals, getProfessionalReviews, type GecontracteerdeProfessional } from '../services/employerTrajectenApi';

/**
 * Trajecten → Gecontracteerde professionals (samenwerkingen).
 * Per professional: profiel, reviews, gemaakte afspraken en lopende dossiers.
 * AVG: dossiers tonen uitsluitend het registratienummer, nooit een naam.
 */
const euro = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });
type Paneel = 'afspraken' | 'dossiers' | 'reviews';

const Reviews: React.FC<{ professionalId: string }> = ({ professionalId }) => {
  const { data, isLoading } = useQuery({ queryKey: ['trajecten-professional-reviews', professionalId], queryFn: () => getProfessionalReviews(professionalId) });
  if (isLoading) return <p className="text-sm text-gray-500">Laden…</p>;
  if (!data?.zichtbaar) {
    return <p className="text-sm text-gray-600">Reviews worden zichtbaar vanaf drie ingezonden evaluaties (privacy van medewerkers). Nog {data?.nog_nodig ?? 3} nodig.</p>;
  }
  return (
    <ul className="space-y-2">
      {data.reviews.map((r) => (
        <li key={r.id} className="border border-gray-200 rounded-md p-3 text-sm">
          <div className="flex items-center justify-between text-gray-500 text-xs mb-1">
            <span>{r.discipline ?? '—'} · {r.periode ?? ''}</span>
            <span className="flex items-center gap-1 font-semibold text-gray-900"><Star className="w-4 h-4 text-[#E5A425] fill-[#E5A425]" />{r.score.toFixed(1)}</span>
          </div>
          {r.toelichting ? <p className="text-gray-700 whitespace-pre-line">{r.toelichting}</p> : <p className="text-gray-400 italic">Geen toelichting</p>}
        </li>
      ))}
    </ul>
  );
};

const ProfessionalKaart: React.FC<{ p: GecontracteerdeProfessional }> = ({ p }) => {
  const [open, setOpen] = useState<Paneel | null>(null);
  const hoofd = p.afspraken[0];
  const tab = (key: Paneel, label: string, Icon: React.ComponentType<{ className?: string }>, aantal?: number) => (
    <button
      type="button"
      onClick={() => setOpen(open === key ? null : key)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border ${open === key ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
    >
      {open === key ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      <Icon className="w-4 h-4" />
      {label}
      {aantal != null && <span className="text-xs text-gray-500">({aantal})</span>}
    </button>
  );

  return (
    <Card>
      <div className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{p.naam}</h3>
            <p className="text-sm text-gray-600">
              {p.afspraken.map((a) => a.functie_label).filter(Boolean).join(', ') || 'Nog geen functie vastgelegd'}
              {hoofd?.effectief_uurtarief != null && <> · {euro.format(hoofd.effectief_uurtarief)}/u</>}
            </p>
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <Star className="w-3.5 h-3.5 text-[#E5A425] fill-[#E5A425]" />
              {p.reviews.zichtbaar && p.reviews.gemiddelde != null ? `${p.reviews.gemiddelde.toFixed(1)} · ${p.reviews.aantal} reviews` : `Score zichtbaar vanaf ${p.reviews.drempel} reviews (${p.reviews.aantal} ontvangen)`}
            </p>
          </div>
          <Link to={`/employer/professional/${p.professional_id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">
            <User className="w-4 h-4" />Profiel bekijken
          </Link>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          {tab('afspraken', 'Gemaakte afspraken', FileSignature, p.afspraken.length)}
          {tab('dossiers', 'Lopende dossiers', FolderOpen, p.lopende_dossiers.length)}
          {tab('reviews', 'Reviews lezen', Star, p.reviews.aantal)}
        </div>

        {open === 'afspraken' && (
          <div className="mt-4 space-y-3">
            {p.afspraken.length === 0 && <p className="text-sm text-gray-600">Nog geen afspraken vastgelegd.</p>}
            {p.afspraken.map((a) => (
              <dl key={a.id} className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm bg-gray-50 rounded-md p-3">
                <div><dt className="text-gray-500 text-xs">Functie</dt><dd className="text-gray-900">{a.functie_label ?? '—'}</dd></div>
                <div><dt className="text-gray-500 text-xs">Uurtarief</dt><dd className="text-gray-900">{a.uurtarief == null ? '—' : euro.format(a.uurtarief)}{a.korting_pct ? ` (−${a.korting_pct}%)` : ''}</dd></div>
                <div><dt className="text-gray-500 text-xs">Effectief tarief</dt><dd className="text-gray-900 font-medium">{a.effectief_uurtarief == null ? '—' : euro.format(a.effectief_uurtarief)}</dd></div>
                <div><dt className="text-gray-500 text-xs">Max. gesprekken per traject</dt><dd className="text-gray-900">{a.max_gesprekken_per_traject ?? '—'}</dd></div>
                <div><dt className="text-gray-500 text-xs">Verslagfrequentie</dt><dd className="text-gray-900">{a.verslag_frequentie ?? '—'}</dd></div>
                <div><dt className="text-gray-500 text-xs">Akkoord</dt><dd className="text-gray-900">{a.akkoord_professional && a.akkoord_opdrachtgever ? `Beide partijen${a.akkoord_op ? ` · ${new Date(a.akkoord_op).toLocaleDateString('nl-NL')}` : ''}` : 'Nog niet wederzijds akkoord'}</dd></div>
                {a.aanvullende_afspraken && <div className="col-span-2 md:col-span-4"><dt className="text-gray-500 text-xs">Aanvullende afspraken</dt><dd className="text-gray-900 whitespace-pre-line">{a.aanvullende_afspraken}</dd></div>}
              </dl>
            ))}
            <Link to={`/employer/connections/${p.connection_id}/agreements`} className="text-sm text-blue-600 hover:underline">Afspraken beheren →</Link>
          </div>
        )}

        {open === 'dossiers' && (
          <div className="mt-4">
            {p.lopende_dossiers.length === 0 ? (
              <p className="text-sm text-gray-600">Geen lopende dossiers.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-4">Registratienummer</th><th className="py-2 pr-4">Discipline</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Gestart</th><th className="py-2">Gesprekken</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {p.lopende_dossiers.map((d) => (
                      <tr key={d.id}>
                        <td className="py-2 pr-4 font-mono text-gray-900">{d.registratienummer}</td>
                        <td className="py-2 pr-4">{d.discipline ?? '—'}</td>
                        <td className="py-2 pr-4">{d.status.replace(/_/g, ' ')}</td>
                        <td className="py-2 pr-4">{d.start_datum ? new Date(d.start_datum).toLocaleDateString('nl-NL') : '—'}</td>
                        <td className="py-2">{d.gesprekken}{d.max_gesprekken ? ` / ${d.max_gesprekken}` : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-gray-500 mt-2">Om privacyredenen tonen wij geen namen van medewerkers, alleen registratienummers.</p>
              </div>
            )}
          </div>
        )}

        {open === 'reviews' && <div className="mt-4"><Reviews professionalId={p.professional_id} /></div>}
      </div>
    </Card>
  );
};

const EmployerGecontracteerdeProfessionalsPage: React.FC = () => {
  useCockpitTitle('Gecontracteerde professionals', 'Alle professionals waarmee uw organisatie afspraken heeft gemaakt');
  const { data, isLoading, error } = useQuery({ queryKey: ['trajecten-professionals'], queryFn: getGecontracteerdeProfessionals });

  return (
    <div className="px-4 sm:px-6 py-6 space-y-4">
      {isLoading ? (
        <p className="text-gray-600 py-12 text-center">Laden…</p>
      ) : error ? (
        <p className="text-red-600 py-12 text-center">De samenwerkingen konden niet worden geladen.</p>
      ) : !data?.length ? (
        <Card>
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-700 mb-1">Nog geen gecontracteerde professionals</p>
            <Link to="/employer/dashboard/zoeken/uitgebreid" className="text-sm text-blue-600 hover:underline">Zoek een professional →</Link>
          </div>
        </Card>
      ) : (
        data.map((p) => <ProfessionalKaart key={p.connection_id} p={p} />)
      )}
    </div>
  );
};

export default EmployerGecontracteerdeProfessionalsPage;
